import { Avatar } from 'module/ui/components/atoms/avatar';
import { HumanisedDate } from 'module/ui/components/atoms/humanised-date';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { Tag } from 'module/ui/components/atoms/tag';
import {
  Body01,
  Body02,
  BodyDark01,
  BodyShort01,
  BoldBody02,
  DarkBody01,
  ProductiveHeading01,
} from 'module/ui/ozone/atoms';
import { faLightbulbOn } from '@fortawesome/pro-solid-svg-icons';
import { CitationType } from 'api/citation/types';
import { SignalType } from 'api/signal/types';
import { SignalCitation } from 'api/signal/types';
import { ReportedBy, SignalField } from 'api/timeline/types';
import { SignalSource } from 'api/timeline/types';
import moment from 'moment';
import { useState } from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import styled from 'styled-components';
import { preprocessMessageWithCitations } from 'utils/citation-preprocessor';
import { FlexWrapperWithShortGap } from 'components/common/styles';
import { CitationDataPopover } from 'components/organisms/document-editor/extensions/citation/ui/citation-data-popover';
import {
  CardHeader,
  CardWrapper,
  TypeIndicator,
  TaskTable,
  TaskHeader,
  TaskRow,
  InsightfulCount,
  SignalMainContentContainer,
  StyledPeopleList,
  AvatarStack,
  BaseAvatar,
  CountAvatar,
  PeopleNames,
  PersonInfo,
  PersonMeta,
  PlaceholderUserNames,
  PlaceholderDate,
  ReasonForSignalContainer,
} from './styles';
import {
  formatUserDisplayName,
  formatRoleDisplay,
  isUserUnknown,
} from './utils';
import {
  MeetingTranscriptDrawer,
  MeetingTranscriptDrawerState,
} from '../meeting-transcript/drawer';
import { signalTypeConfigs } from '../signal/signal-type/type-configs';

interface TaskAssignee {
  name: string;
  avatarUrl?: string;
  role?: string;
}

interface Task {
  name: string;
  assignee: TaskAssignee;
  dueDate: string;
}

interface SignalOutputCardProps {
  type: string;
  people: ReportedBy[] | ReportedBy;
  reason?: SignalField;
  title: string;
  description: string;
  tasks?: Task[];
  insightfulContent?: string;
  className?: string;
  sourceId?: number;
  showCompanyDetails?: boolean;
  companyLogo?: string;
  companyName?: string;
  createdAt?: Date;
  signalType?: SignalType;
  citations?: SignalCitation;
  sourceType?: SignalSource;
}

interface PeopleListProps {
  people: ReportedBy[];
  maxDisplay?: number;
}

const StyledCitationPopover = styled(CitationDataPopover)`
  display: inline-block;
`;

export const SignalOutputCard = ({
  type,
  people,
  reason,
  title,
  description,
  tasks,
  insightfulContent,
  className,
  sourceId,
  sourceType,
  showCompanyDetails = false,
  companyLogo,
  companyName,
  createdAt,
  signalType = SignalType.Operational,
  citations,
}: SignalOutputCardProps) => {
  const signalTypeConfig = signalTypeConfigs[signalType];
  const [drawerState, setDrawerState] =
    useState<MeetingTranscriptDrawerState>(null);

  const createMarkdownComponents = (): Partial<Components> => ({
    cite: (props: any) => {
      const citationNum = props['data-num'];

      return (
        <StyledCitationPopover
          label={citationNum}
          citationId={citations?.[citationNum]?.citationUuid ?? 0}
          citationType={citations?.[citationNum]?.citationType as CitationType}
          onViewCitationSource={(params) => {
            if (params.citationType === CitationType.Meeting) {
              setDrawerState({
                meetingId: params.citation.metaData.source.meetingId,
                source: params.citation.metaData.source,
                timeToSeek: parseInt(params.currentBlock?.fromTime ?? '0'),
              });
            }
          }}
        />
      );
    },
  });

  const markdownComponents = createMarkdownComponents();

  return (
    <CardWrapper
      className={className}
      // if meetingId is not provided, don't create an id
      id={
        sourceId
          ? `signal-${type?.toLowerCase()}-${sourceType} - ${sourceId}`
          : undefined
      }
    >
      <CardHeader>
        <TypeIndicator>
          {signalTypeConfig.icon}
          <Body01>{type}</Body01>
        </TypeIndicator>
      </CardHeader>

      {Array.isArray(people) && people.length > 1 ? (
        <PeopleList people={people} />
      ) : (
        <PersonInfo>
          {showCompanyDetails ? (
            <Avatar
              size="large"
              // companyLogoUrl="thumb_image.png"
              src={companyLogo}
              // name={formatUserDisplayName(people as ReportedBy)}
              isUserUnknown={isUserUnknown(people as ReportedBy)}
            />
          ) : (
            <Avatar
              size="large"
              name={formatUserDisplayName(people as ReportedBy)}
              isUserUnknown={isUserUnknown(people as ReportedBy)}
            />
          )}
          {showCompanyDetails ? (
            <PersonMeta>
              <BodyShort01>{companyName}</BodyShort01>
              <PlaceholderUserNames>
                {formatUserDisplayName(people as ReportedBy)}{' '}
                <div>{formatRoleDisplay(people as ReportedBy) ? '·' : ''}</div>{' '}
                {formatRoleDisplay(people as ReportedBy)}
              </PlaceholderUserNames>
              <PlaceholderDate>
                <HumanisedDate timestamp={moment(createdAt)} />
              </PlaceholderDate>
            </PersonMeta>
          ) : (
            <PersonMeta>
              <BodyShort01>
                {formatUserDisplayName(people as ReportedBy)}
              </BodyShort01>
              <Body02>{formatRoleDisplay(people as ReportedBy)}</Body02>
            </PersonMeta>
          )}
        </PersonInfo>
      )}

      {reason &&
        reason?.metaData?.options?.find(
          (option) => option.value === reason?.fieldValue,
        )?.label && (
          <ReasonForSignalContainer>
            <DarkBody01>Reason</DarkBody01>
            <FlexWrapperWithShortGap>
              <Tag color={signalTypeConfig.color}>
                {
                  reason?.metaData?.options?.find(
                    (option) => option.value === reason?.fieldValue,
                  )?.label
                }
              </Tag>
            </FlexWrapperWithShortGap>
          </ReasonForSignalContainer>
        )}

      <SignalMainContentContainer>
        <ProductiveHeading01>{title}</ProductiveHeading01>
        <BodyShort01>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[[rehypeRaw]]}
            components={markdownComponents}
          >
            {preprocessMessageWithCitations(description)}
          </ReactMarkdown>
        </BodyShort01>
      </SignalMainContentContainer>

      {tasks && tasks.length > 0 && (
        <TaskTable>
          <TaskHeader>
            <BoldBody02>Name</BoldBody02>
            <BoldBody02>Assignee</BoldBody02>
            <BoldBody02>Due date</BoldBody02>
          </TaskHeader>
          {tasks.map((task, index) => (
            <TaskRow key={index}>
              <BodyDark01>{task.name}</BodyDark01>
              <FlexWrapperWithShortGap>
                <Avatar
                  size="small"
                  name={task.assignee.name}
                  src={task.assignee.avatarUrl}
                />
                <BodyDark01>{task.assignee.name}</BodyDark01>
              </FlexWrapperWithShortGap>
              <BodyDark01>{task.dueDate}</BodyDark01>
            </TaskRow>
          ))}
        </TaskTable>
      )}

      {insightfulContent && (
        <InsightfulCount>
          <FontAwesomeIcon icon={faLightbulbOn} />
          <DarkBody01>{insightfulContent}</DarkBody01>
        </InsightfulCount>
      )}
      <MeetingTranscriptDrawer
        drawerState={drawerState}
        onClose={() => setDrawerState(null)}
      />
    </CardWrapper>
  );
};

const PeopleList: React.FC<PeopleListProps> = ({ people }) => {
  const remainingCount = people.length - 1;
  const [firstPerson] = people;

  return (
    <StyledPeopleList>
      <AvatarStack>
        <BaseAvatar
          size="default"
          name={`${firstPerson.firstName} ${firstPerson.lastName}`}
          // src={firstPerson.photoUrl}
          // src={'/images/avatar.png'}
        />
        <CountAvatar>{remainingCount}</CountAvatar>
      </AvatarStack>
      <PeopleNames>
        <PersonMeta>
          {people.map((person, index) => (
            <>
              {formatUserDisplayName(person)}
              {index < people.length - 1 && ', '}
            </>
          ))}
        </PersonMeta>
        <Body02>View members</Body02>
      </PeopleNames>
    </StyledPeopleList>
  );
};
